import moment from 'moment'
import ms from 'ms'
import { closingTimeOptions } from '@/utils/consts'
import { dateFormat } from '@/utils/regex'
import i18n, { langs } from '@/utils/i18n'
import 'moment/locale/zh-cn'

export const CH_CALENDAR_DATE = 'YY年 MM月 DD日'
export const ONE_DAY_MILLI = 24 * 60 * 60 * 1000
export const DD_MMM_YYYY = 'DD MMM YYYY'
export const DD_MMM_YY_HH_MM = 'DD MMM YY HH:mm'

const localeLongDateFormat = {
  [langs.enUS]: {
    longDateFormat: {
      'MMM YY': 'MMM YY',
      LT: 'HH:mm',
      LTS: 'HH:mm a', // amPmTimeFormat
      L: 'DD MMM YY',
      LL: 'DD MMM YYYY', // DD_MMM_YYYY
      LLL: 'DD MMM YY - HH:mm', // dateformat
      LLLL: 'DD MMM YY, HH:mm',
      llll: 'DD MMM YY HH:mm:ss',
      'L LT': 'DD MMM YY HH:mm',
      lll: 'DD MMM YYYY HH:mm', // fullYearTimeFormat
      ll: 'DD/MMM/YY HH:mm', // emailDateFormat
      l: 'DD-MMM-YYYY', // fullYearDateFormat
    },
  },
  [langs.zhCN]: {
    longDateFormat: {
      'MMM YY': 'YY年 MM月',
      LT: 'HH:mm',
      LTS: 'HH:mm a',
      L: 'YY年 MM月 DD日',
      LL: 'YYYY年 MM月 DD日',
      LLL: 'YY年 MM月 DD日 - HH:mm',
      LLLL: 'YY年 MM月 DD日, HH:mm',
      'L LT': 'YY年 MM月 DD日 HH:mm',
      llll: 'YY年 MM月 DD日 HH:mm:ss',
      lll: 'YYYY年 MM月 DD日 HH:mm',
      ll: 'YY年 MM月 DD日 HH:mm',
      l: 'YYYY年 MM月 DD日',
    },
  },
}
// Loading the customized translations into the Moment.js
moment.defineLocale(langs.zhCN, {
  parentLocale: 'zh-cn',
  longDateFormat: localeLongDateFormat[langs.zhCN].longDateFormat,
})
moment.defineLocale(langs.enUS, {
  parentLocale: 'en',
  longDateFormat: localeLongDateFormat[langs.enUS].longDateFormat,
})

function getLongDateFormat (format) {
  const entry = Object.entries(localeLongDateFormat[langs.enUS].longDateFormat).find(entry => entry[1] === format) || []
  return entry[0] || 'LLL'
}

function getHours (time) {
  let hours = ''
  if (!time) {
    return hours
  } else if (time instanceof Date) {
    hours = time.getHours()
  } else if (typeof time === 'string') {
    const date = new Date(time)
    hours = date.getHours()
  }
  return hours
}

function getMinutes (time) {
  let min = ''
  if (!time) {
    return min
  } else if (time instanceof Date) {
    min = time.getMinutes()
  } else if (typeof time === 'string') {
    const date = new Date(time)
    min = date.getMinutes()
  }
  return min
}

function getDateInMonth (time) {
  let dateInMonth = ''
  if (!time) {
    return dateInMonth
  } else if (time instanceof Date) {
    dateInMonth = time.getDate()
  } else if (typeof time === 'string') {
    const date = new Date(time)
    dateInMonth = date.getDate()
  }
  return dateInMonth
}

export default {
  DD_MMM_YYYY,
  DD_MMM_YY_HH_MM,

  /**
   * @template {keyof localeLongDateFormat[enLocale]['longDateFormat']} LD
   * @param {Date|String} date
   * @param {LD} [longFormatKeyName='L'] HKLand Custom Format
   * @returns {String}
   *
   * @example
   * 'MMM YY': 'MMM YY',
      LT: 'HH:mm',
      LTS: 'HH:mm a', // amPmTimeFormat
      L: 'DD MMM YY',
      LL: 'DD MMM YYYY', // DD_MMM_YYYY
      LLL: 'DD MMM YY - HH:mm', // dateformat
      LLLL: 'DD MMM YY, HH:mm',
      llll: 'DD MMM YY HH:mm:ss',
      'L LT': 'DD MMM YY HH:mm',
      lll: 'DD MMM YYYY HH:mm', // fullYearTimeFormat
      ll: 'DD/MMM/YY HH:mm', // emailDateFormat
      l: 'DD-MMM-YYYY', // fullYearDateFormat
   */
  formatByLongDate (date, longFormatKeyName = 'L') {
    if (!date) return null
    const curLanguage = moment.locale()
    const customFormat = localeLongDateFormat[curLanguage]['longDateFormat'][longFormatKeyName]
    return moment(date).format(customFormat)
  },

  /**
   * TODO There are many duplicated caller to call the moment() in the code base. E.g.
   * const d = toMoment(date), const newDate = parseClosingDateTime(d)
   * convert a date to a moment object
   * @param {Date} date
   * @param {String} format, provide dates format if it's not ISO recognised format which may cause warning in IE
   */
  toMoment (date, format) {
    return moment(date, format)
  },

  /**
   * Format a date
   * @param {Date|string} date - a date object
   * @param {String} format - date format
   * @return {String} formatted date
   */
  format (date, format) {
    if (!date) return null
    const longDateFormat = getLongDateFormat(format)
    const d = moment(date)
    if (d.isValid()) {
      return moment(date).format(longDateFormat)
    }
    return null
  },

  /**
   * Convert a date (after formatted) to separated date and time,
   *  e.g. formatted date: 20170101-13:00 converted to: ['20170101', '13:00']
   * @param {Date} date - date object
   * @param {String} format date format
   * @param {String} delimiter the delimiter used to split the date
   */
  convertDateToArray (date, format, delimiter) {
    const convertedDate = this.format(date, format)
    if (!convertedDate) {
      return null
    }
    return convertedDate.split(delimiter).map(item => item.trim())
  },

  /**
   * Format a date String
   *  e.g. 2017-09-07T01:30:21.000Z converted to: 07 Sep 2017 01:30
   * @param {date|string} dateString - date string
   * @param {boolean} [includeTime] whether return with time
   */
  formatDate (dateString, includeTime = false, lineBreak = false) {
    const dateArray = this.convertDateToArray(dateString, null, '-')
    const delimiter = lineBreak ? '\n' : ' '
    if (includeTime) {
      return (dateArray && dateArray.length >= 2) ? dateArray[0].trim().concat(delimiter).concat(dateArray[1]) : ''
    } else {
      return (dateArray && dateArray.length >= 2) ? dateArray[0].trim() : ''
    }
  },

  isBeforeEndDate (endDate) {
    const now = new Date()
    const isEndDateValid = endDate ? (+new Date(endDate) + ONE_DAY_MILLI) > +now : true
    return isEndDateValid
  },

  parse (dateString, format = 'DD MMM YY HH:mm') {
    const localeFormat = this.getFormatByLang(format)
    return moment(dateString, localeFormat)
  },

  getEndTimeOfDay (date) {
    const result = new Date(date)
    result.setHours(23, 59, 59, 999)
    return result
  },

  getFormatByLang (format) {
    const longDateFormat = getLongDateFormat(format)
    const localeFormat = moment.localeData().longDateFormat(longDateFormat)
    return localeFormat
  },

  isEffective (startDate, endDate, includeEnd = true) {
    const now = new Date()
    const shift = includeEnd ? ONE_DAY_MILLI : 0
    const isStartValid = startDate ? +new Date(startDate) < +now : false
    const isEndValid = endDate ? (+new Date(endDate) + shift) > +now : true
    return isStartValid && isEndValid
  },

  ONE_DAY_MILLI,

  /**
   * add additional hours to a date
   * @param {Date} date Date to add hours
   * @param {String} time
   */
  addClosingTime (date, time) {
    if (!date) return
    switch (time) {
      case closingTimeOptions[0]:
        date.add(12, 'hours')
        break
      case closingTimeOptions[1]:
        date.add(17, 'hours')
        break
      default:
    }
    return date
  },

  /**
   * parse the closing date to a date string and time string
   * @param {Date} date
   * @param {String} format
   * @param {String} delimiter default '-'
   */
  parseClosingDateTime (date, format, delimiter = '-', isClosingDateTime = true) {
    if (!format) {
      format = dateFormat
    }
    const convertedClosingDateTime = this.convertDateToArray(date, format, delimiter)
    if (!convertedClosingDateTime) return [null, null]

    const closingDate = convertedClosingDateTime[0]
    const time = convertedClosingDateTime[1].trim()
    const closingTime = isClosingDateTime ? (time === '00:00') ? closingTimeOptions[0]
      : (time === '12:00') ? closingTimeOptions[0] : closingTimeOptions[1]
      : time
    return [closingDate, closingTime]
  },

  generateTimeDurations (interval, startTime = '00:00', endTime = '24:00') {
    const date = moment(startTime, 'HH:mm')
    const endDate = moment(endTime, 'HH:mm')
    let timeList = []
    while (date.isSameOrBefore(endDate)) {
      const hour = date.hours() < 10 ? `0${date.hours()}` : date.hours()
      const minute = date.minutes() < 10 ? `0${date.minutes()}` : date.minutes()
      timeList.push(`${hour}:${minute}`)
      date.add(interval, 'minute')
    }
    if (endTime === '24:00' && timeList[timeList.length - 1] === '00:00') {
      timeList[timeList.length - 1] = '23:59'
    }
    return timeList
  },

  getTimeOptions (date, interval = 30, startTime = '00:00') {
    let start = startTime
    if (date) {
      let scheduleDate = new Date(date)
      scheduleDate.setHours(0, 0, 0)
      if (scheduleDate <= new Date()) {
        const currentTime = moment(new Date(), 'HH:mm')
        let startHour = currentTime.hours()
        let startMin = Math.ceil((currentTime.minutes() / interval)) * interval
        if (startMin === 60) {
          startHour += 1
          startMin = 0
        }
        if (startHour >= 24) {
          startHour = 24
          startMin = 0
        }
        start = `${startHour}:${startMin}`
      }
    }
    return this.generateTimeDurations(interval, start)
  },

  convertArrayToDate (date, time, format) {
    const timeArray = time.split(':')
    const longDateFormat = getLongDateFormat(format)
    return moment(date, longDateFormat).hours(Number(timeArray[0])).minutes(Number(timeArray[1]))
  },

  /**
   * convert the time to include labels, e.g. 12:00 => 12:00 Noon
   * @param {Date} date
   * @param {String} format
   * @param {String} delimiter default '-'
   */
  parseClosingTime (date, format, delimiter = '-') {
    if (!format) {
      format = dateFormat
    }
    const formattedDate = this.convertDateToArray(date, format, delimiter)
    if (formattedDate && formattedDate[1].trim() === '12:00') {
      formattedDate[1] = '12:00 Noon'
    }
    return formattedDate
  },
  isWithinOfficeHours (startTime = '', endTime = '') {
    if (!startTime && !endTime) {
      // startTime, endTime type should be Date or String
      return null
    }

    const startDay = getDateInMonth(startTime)
    const endDay = getDateInMonth(endTime)
    if (startDay !== endDay) {
      return false
    }

    const startHour = getHours(startTime)
    const endHour = getHours(endTime)
    const endMin = getMinutes(endTime)
    const isEndTimeValidate = endHour >= 9 && (endHour < 17 || (endHour === 17 && endMin === 0))
    if (startHour >= 9 && startHour <= 17 && (!endHour || isEndTimeValidate)) {
      return true
    }
    return false
  },
  dateSortFunc (propName, isAsc = true) {
    return (prevObj, nextObj) => {
      if (isAsc) {
        return nextObj[propName] > prevObj[propName] ? -1 : 1
      } else {
        return nextObj[propName] > prevObj[propName] ? 1 : -1
      }
    }
  },
  getTimeMillis (dateString, defaultValue = Number.MIN_SAFE_INTEGER) {
    if (!dateString) {
      return defaultValue
    }
    const date = moment(dateString)
    if (!date.isValid()) {
      return defaultValue
    }
    return date.valueOf()
  },
  getTodayStartDateObject () {
    return new Date(new Date().setHours(0, 0, 0, 0))
  },
  getStartOfDay (date) {
    const time = new Date(date)
    if (isNaN(time)) return new Date().getTime()
    return time.setHours(0, 0, 0, 0)
  },
  getEndOfDay (date) {
    const result = moment(date)
    return result.isValid() ? result.endOf('day').toDate() : null
  },
  transTimeUnitByLang (time) {
    const formattedTime = ms(ms(time), { long: true })
    if (typeof formattedTime === 'string') {
      const timeArray = formattedTime.split(' ')
      const dateTimeWithLang = i18n.t(`dateTime.${timeArray[1]}`)
      return `${timeArray[0]} ${dateTimeWithLang}`
    } else {
      console.warn(`Time Format error: ${time} can not translate to correct time format`)
      return time
    }
  },
  yesterday () {
    const date = new Date()
    date.setDate(date.getDate() - 1)
    return date
  },
}
